import { useMemo, useState } from 'react';
import { IconGlyph } from '../../ui/Primitives.jsx';
import { fillTemplate } from '../../../lib/helpers.js';
import { t } from '../../../i18n/strings.js';
import { inferPlanSettingsFromAnswers } from '../../../config/aiPlanAdvisor.js';
import { generateWorkoutPlan } from '../../../config/planGenerator.js';
import { getExercisesBySlugs } from '../../../config/exercisesConfig.js';
import { computeCaloriesBurned } from '../../../lib/calories.js';
import { GRADIENT_BTN } from './shared.js';

const KNOWN_GOALS = ['condicionamento', 'forca', 'emagrecer', 'hipertrofia'];

// Prévia 100% real: o plano vem do mesmo motor do app (aiPlanAdvisor +
// planGenerator + exercisesConfig + calories) alimentado pelas respostas do
// quiz. Sem dados → a seção inteira some (nunca mostra número inventado).
export default function PersonalizedPlanPreview({ answers, gender, onUnlock }) {
  const [selectedDay, setSelectedDay] = useState(0);
  const planData = useMemo(() => {
    const inferred = inferPlanSettingsFromAnswers(answers ?? {});
    if (!inferred) return null;
    const plan = generateWorkoutPlan(inferred.settings);
    if (!plan.days?.length) return null;
    const days = plan.days
      .filter((d) => d.slugs?.length)
      .map((d) => {
        const exercises = getExercisesBySlugs(d.slugs, gender);
        const minutes = d.estimatedMinutes ?? 30;
        return {
          minutes,
          calories: computeCaloriesBurned({ durationSeconds: minutes * 60, weightKg: answers?.weight_current }),
          exercises,
          muscles: [...new Set(exercises.map((e) => e.muscleGroup).filter(Boolean))].slice(0, 3),
        };
      });
    return days.length ? { days } : null;
  }, [answers, gender]);

  if (!planData) return null;
  const day = planData.days[Math.min(selectedDay, planData.days.length - 1)];
  const visibleEx = day.exercises.slice(0, 3);
  const blurredEx = (() => {
    const rest = day.exercises.slice(3);
    if (rest.length >= 2) return rest.slice(0, 2);
    const next = planData.days[(selectedDay + 1) % planData.days.length];
    const filler = next && next !== day ? next.exercises : [];
    return [...rest, ...filler].slice(0, 2);
  })();
  const goalLabel = KNOWN_GOALS.includes(answers?.goal) ? t(`paywallIntl.goal_${answers.goal}`) : null;
  const scrollToPricing = onUnlock;

  return (
    <>
          <div className="mt-5 rounded-[22px] bg-surface p-4 border border-white/5">
            {/* topo do "app" */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 h-11 pl-3 pr-4 rounded-full bg-surface-2">
                <IconGlyph name="ti-barbell" size={18} className="text-cyan" />
                <span className="font-display font-bold text-[15px]">{t('paywallIntl.myPlan')}</span>
                <IconGlyph name="ti-chevron-down" size={14} className="text-ink-muted" />
              </div>
              <div className="flex gap-2" aria-hidden="true">
                <div className="w-11 h-11 rounded-full bg-surface-2 flex items-center justify-center">
                  <IconGlyph name="ti-chevron-down" size={16} className="text-ink-muted rotate-180" />
                </div>
                <div className="w-11 h-11 rounded-full bg-surface-2 flex items-center justify-center">
                  <IconGlyph name="ti-adjustments" size={16} className="text-ink-muted" />
                </div>
              </div>
            </div>

            {/* abas dos dias (geradas pelo plano real) */}
            <div className="flex gap-1 mt-4 overflow-x-auto -mx-1 px-1 pb-1" role="tablist">
              {planData.days.map((_, i) => (
                <button
                  key={i}
                  role="tab"
                  aria-selected={i === selectedDay}
                  onClick={() => setSelectedDay(i)}
                  className={`shrink-0 h-10 px-4 rounded-full font-display font-extrabold text-[13px] tracking-wide transition-colors ${
                    i === selectedDay ? 'text-bg' : 'text-ink-muted'
                  }`}
                  style={i === selectedDay ? { background: GRADIENT_BTN } : undefined}
                >
                  {fillTemplate(t('paywallIntl.dayTab'), { n: i + 1 })}
                </button>
              ))}
            </div>
            <div className="h-px bg-white/5 mt-2" />

            {/* configuração do treino */}
            <div className="text-[11px] font-bold tracking-wider text-ink-muted mt-4">
              {fillTemplate(t('paywallIntl.workoutSettings'), { n: selectedDay + 1 })}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {goalLabel && (
                <span className="h-9 px-3.5 rounded-full bg-surface-2 text-[13px] font-semibold flex items-center gap-1.5">
                  <IconGlyph name="ti-trophy" size={14} className="text-cyan" /> {goalLabel}
                </span>
              )}
              <span className="h-9 px-3.5 rounded-full bg-surface-2 text-[13px] font-semibold flex items-center">
                {fillTemplate(t('paywallIntl.chipMinutes'), { n: day.minutes })}
              </span>
              <span className="h-9 px-3.5 rounded-full bg-surface-2 text-[13px] font-semibold flex items-center">
                {fillTemplate(t('paywallIntl.chipMuscles'), { n: day.muscles.length })}
              </span>
            </div>

            {/* músculos-alvo (reais) */}
            <div className="text-[11px] font-bold tracking-wider text-ink-muted mt-4">{t('paywallIntl.targetMuscles')}</div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {day.muscles.map((m) => (
                <div key={m} className="rounded-2xl bg-surface-2 border border-white/5 py-2.5 flex flex-col items-center gap-1">
                  <IconGlyph name="ti-shield-check" size={18} className="text-cyan" />
                  <span className="text-[12px] font-medium">{t(`paywallIntl.muscle_${m}`)}</span>
                </div>
              ))}
            </div>

            {/* lista de exercícios (linhas de app, não botões) */}
            <div className="flex items-center justify-between mt-4">
              <span className="text-[11px] font-bold tracking-wider text-ink-muted">
                {fillTemplate(t('paywallIntl.exercisesCount'), { n: day.exercises.length })}
              </span>
              <span className="text-[13px] font-semibold text-ink-muted">{t('paywallIntl.edit')}</span>
            </div>

            <div className="mt-1">
              {visibleEx.map((ex) => (
                <ExerciseRow key={ex.slug} ex={ex} />
              ))}
            </div>

            {/* conteúdo desfocado + degradê = "tem mais atrás" */}
            {blurredEx.length > 0 && (
              <div className="relative" aria-hidden="true">
                <div className="pointer-events-none select-none" style={{ filter: 'blur(5px)', opacity: 0.8 }}>
                  {blurredEx.map((ex) => (
                    <ExerciseRow key={ex.slug} ex={ex} noVideo />
                  ))}
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-surface/60 to-surface" />
              </div>
            )}

            <div className="relative z-10 text-center pt-1">
              <div className="font-display font-bold text-[17px]">{t('paywallIntl.unlockTitle')}</div>
              <p className="text-[13px] text-ink-muted mt-1">{t('paywallIntl.unlockSub')}</p>
              <button
                onClick={scrollToPricing}
                className="fc-cta-pulse mt-3 w-full h-[54px] rounded-2xl font-display font-extrabold text-[15px] text-bg active:scale-[0.98] transition-transform"
                style={{ background: GRADIENT_BTN, boxShadow: '0 6px 20px rgba(25,215,242,0.3)' }}
              >
                {t('paywallIntl.unlockCta')}
              </button>
            </div>
          </div>

          <div className="mt-3 flex items-center rounded-2xl bg-surface py-3 border border-white/5">
            <StatCol value={String(planData.days.length)} label={t('paywallIntl.statLineWorkouts')} />
            <div className="w-px h-7 bg-white/10" />
            <StatCol value={`${planData.days[0].minutes} min`} label={t('paywallIntl.statLineMinutes')} />
            <div className="w-px h-7 bg-white/10" />
            <StatCol value={String(planData.days[0].calories)} label={t('paywallIntl.statLineKcal')} />
          </div>
    </>
  );
}

function ExerciseRow({ ex, noVideo = false }) {
  const reps = ex.targetReps;
  const sub =
    reps > 1
      ? fillTemplate(t('paywallIntl.setsReps'), { sets: '3', reps: String(reps) })
      : fillTemplate(t('paywallIntl.setsOnly'), { sets: '3' });
  return (
    <div className="flex items-center gap-3 py-3 border-b border-white/5 last:border-b-0">
      <div className="w-14 h-14 shrink-0 rounded-xl overflow-hidden bg-white">
        {!noVideo && ex.video && (
          <video
            src={`${ex.video}#t=0.6`}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[15px] leading-snug truncate">{ex.name}</div>
        <div className="text-[12px] text-ink-muted mt-0.5">{sub}</div>
      </div>
      <IconGlyph name="ti-dots-vertical" size={18} className="text-ink-muted shrink-0" />
    </div>
  );
}

function StatCol({ value, label }) {
  return (
    <div className="flex-1 flex flex-col items-center">
      <span className="font-display text-[17px] font-extrabold">{value}</span>
      <span className="text-[11px] text-ink-muted mt-0.5">{label}</span>
    </div>
  );
}
